import classnames from 'classnames';
import { InlineButton } from '@picsart/design-system/components/InlineButton';
import { IconPlay } from '@picsart/design-system/foundation/icons/IconPlay';
import { IconSoundOff } from '@picsart/design-system/foundation/icons/IconSoundOff';
import { IconSoundOn } from '@picsart/design-system/foundation/icons/IconSoundOn';
import useStyles from './styles';
import CardAudio from '../CardAudio';
import MediaCarousel from '../MediaCarousel';
import type { CardMediaProps } from './types';

const VideoPlayIcon = <IconPlay fill="currentColor" width={12} height={12} />;

const CardMedia = ({
  videoRef,
  imageUrl,
  previews,
  title,
  isVideo,
  isAudio,
  poster,
  hideVideoIcon,
  showMuteButton,
  muteButtonPlacement = 'bottom-right',
  mediaLoaded,
  shouldLoadVideo,
  videoReady,
  isMuted,
  hasAudio,
  onImageLoad,
  onVideoLoad,
  onLoadedMetadata,
  onCanPlay,
  onMuteToggle,
}: CardMediaProps) => {
  const classes = useStyles();
  const imageClassName = classnames(classes.image, {
    [classes.imageLoaded]: mediaLoaded,
  });
  const videoClassName = classnames(classes.video, {
    [classes.videoLoaded]: mediaLoaded,
  });

  const isMuteTopRight = muteButtonPlacement === 'top-right';

  const renderSkeleton = !mediaLoaded && (
    <div
      className={classes.mediaLoadingSkeleton}
      data-testid="card-media-skeleton"
    />
  );

  const renderMuteButton = showMuteButton && hasAudio && videoReady && (
    <span
      className={classnames(classes.muteButtonWrapper, {
        [classes.muteButtonWrapperTopRight]: isMuteTopRight,
      })}
    >
      <InlineButton
        size="large"
        centerIcon={isMuted ? IconSoundOff : IconSoundOn}
        className={classes.iconButton}
        isWithoutBorder
        isWithoutBackground
        onClick={onMuteToggle}
        aria-label={isMuted ? 'Unmute' : 'Mute'}
        dataTestId="card-media-mute-toggle"
      />
    </span>
  );

  const renderVideoIcon = !hideVideoIcon && (
    <div className={classes.videoIcon}>{VideoPlayIcon}</div>
  );

  if (isAudio) {
    return <CardAudio />;
  }

  // Ahead of the video branches on purpose. A multi-output preset cycles its
  // previews instead of autoplaying only the first: the tile then advertises
  // every output, and a tile that swapped to one playing video once the delayed
  // load fired would read as a glitch. A video preset's slides do play — the
  // carousel keeps only the visible one live, so the card costs one decoder.
  if (previews) {
    const { items } = previews;
    return (
      <>
        {renderSkeleton}
        <div className={imageClassName}>
          <MediaCarousel
            fill
            items={items.map(({ url, videoUrl }, index) => ({
              key: videoUrl ?? url,
              src: url,
              videoSrc: videoUrl,
              alt: `${title || 'Preset'} ${index + 1} of ${items.length}`,
            }))}
          />
        </div>
        <img
          className={classes.mosaicLoadProbe}
          src={items[0].url}
          alt=""
          aria-hidden="true"
          loading="lazy"
          onLoad={onImageLoad}
        />
      </>
    );
  }

  // Video with poster - delayed loading
  if (isVideo && shouldLoadVideo) {
    return (
      <>
        {renderSkeleton}
        {poster && (
          <img
            className={imageClassName}
            src={poster}
            alt={title || 'Video preview'}
          />
        )}
        <video
          ref={videoRef}
          className={videoClassName}
          style={{ opacity: videoReady ? 1 : 0 }}
          muted
          poster={poster}
          loop
          playsInline
          controls={false}
          preload="auto"
          data-testid="card-media-video"
          onLoadedData={onVideoLoad}
          onLoadedMetadata={onLoadedMetadata}
          onCanPlay={onCanPlay}
        >
          <track kind="captions" />
          <source src={imageUrl} type="video/mp4" />
        </video>
        {renderVideoIcon}
        {renderMuteButton}
      </>
    );
  }

  // Video with poster - not yet loading video
  if (isVideo && poster) {
    return (
      <>
        {renderSkeleton}
        <img
          className={imageClassName}
          src={poster}
          alt={title || 'Video preview'}
          loading="lazy"
          onLoad={onImageLoad}
        />
        {renderVideoIcon}
      </>
    );
  }

  // Video without poster - not yet loading video. Rendering the element here
  // would bypass the visibility gate and download the whole file on mount.
  if (isVideo) {
    return (
      <>
        {renderSkeleton}
        {renderVideoIcon}
      </>
    );
  }

  // Image
  return (
    <>
      {renderSkeleton}
      <img
        className={imageClassName}
        src={imageUrl}
        alt={title || 'Preset'}
        loading="lazy"
        onLoad={onImageLoad}
      />
    </>
  );
};

export default CardMedia;
